import React, { Component, ErrorInfo, ReactNode, lazy, Suspense } from 'react';
import { LazyMotion, domAnimation } from 'framer-motion';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import { captureException } from './sentry';
import { Layout } from './components/Layout';
const HomePage = lazy(() => import('./pages/HomePage'));

const PlatformPage = lazy(() => import('./pages/PlatformPage'));
const AIBuildsPage = lazy(() => import('./pages/AIBuildsPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PricingRequestSuccess = lazy(() => import('./pages/PricingRequestSuccess'));
const ProposalSuccess = lazy(() => import('./pages/ProposalSuccess'));
const ProposalCancel = lazy(() => import('./pages/ProposalCancel'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ContactSuccess = lazy(() => import('./pages/ContactSuccess'));
const ContactPersonalEmail = lazy(() => import('./pages/ContactPersonalEmail'));
const PersonalEmailNotice = lazy(() => import('./pages/PersonalEmailNotice'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const DemoVideoPage = lazy(() => import('./pages/DemoVideoPage'));
const MaxPage = lazy(() => import('./pages/MaxPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const GdprPage = lazy(() => import('./pages/GdprPage'));
const DpaPage = lazy(() => import('./pages/DpaPage'));
const EulaPage = lazy(() => import('./pages/EulaPage'));
const ThirdPartyServicesPage = lazy(() => import('./pages/ThirdPartyServicesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const SuccessStoriesPage = lazy(() => import('./pages/SuccessStoriesPage'));
const SuccessStoryPage = lazy(() => import('./pages/SuccessStoryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Product Pages
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductMarketplacePage = lazy(() => import('./pages/ProductMarketplacePage'));
const ProductServicesPage = lazy(() => import('./pages/ProductServicesPage'));
const ProductLauncherPage = lazy(() => import('./pages/ProductLauncherPage'));
const ProductBookingsPage = lazy(() => import('./pages/ProductBookingsPage'));
const ProductStorePage = lazy(() => import('./pages/ProductStorePage'));
const ProductCustomPage = lazy(() => import('./pages/ProductCustomPage'));
const ProductMatchmakerPage = lazy(() => import('./pages/ProductMatchmakerPage'));
const WhiteLabelAIAgentMarketplacePage = lazy(
  () => import('./pages/WhiteLabelAIAgentMarketplacePage')
);
const ProductCommoditiesPage = lazy(() => import('./pages/ProductCommoditiesPage'));
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'));
const SupportHoursPage = lazy(() => import('./pages/SupportHoursPage'));

/**
 * Custom Error Boundary that works with lazy-loaded Sentry.
 * Captures errors via our sentry module which queues them if Sentry isn't loaded yet.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Report to Sentry (queued if not loaded yet)
    captureException(error);
    // Log for debugging
    console.error('React Error Boundary caught:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Something went wrong</h1>
          <p>An error occurred. Our team has been notified.</p>
          <details
            style={{ marginTop: '1rem', textAlign: 'left', maxWidth: '600px', margin: '1rem auto' }}
          >
            <summary>Error details</summary>
            <pre
              style={{
                background: '#f3f4f6',
                padding: '1rem',
                overflow: 'auto',
                fontSize: '0.875rem',
              }}
            >
              {this.state.error?.toString()}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const LegacyBlogRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const target = slug ? `/marketplace-insights/${slug}` : '/marketplace-insights';
  return <Navigate to={target} replace />;
};

// Sentry debug page - only available in development
const SentryDebugPage: React.FC = () => {
  const throwError = () => {
    throw new Error('This is a test error from the React Sentry debug page!');
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
      <h1>Sentry Debug</h1>
      <p style={{ marginBottom: '1rem' }}>
        Click the button below to trigger a test error that will be captured by Sentry.
      </p>
      <button
        onClick={throwError}
        style={{
          background: '#dc2626',
          color: 'white',
          padding: '0.75rem 1.5rem',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          fontSize: '1rem',
        }}
      >
        Trigger Test Error
      </button>
      <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
        After clicking, check your Sentry dashboard for the captured error.
      </p>
    </div>
  );
};

const PageLoader = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
    }}
  >
    <div style={{ color: '#fff', fontSize: '1.2rem' }}>Loading...</div>
  </div>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: 'platform',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PlatformPage />
          </Suspense>
        ),
      },
      {
        path: 'ai-powered-launch',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AIBuildsPage />
          </Suspense>
        ),
      },
      {
        path: 'features',
        element: (
          <Suspense fallback={<PageLoader />}>
            <FeaturesPage />
          </Suspense>
        ),
      },
      {
        path: 'how-it-works',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HowItWorksPage />
          </Suspense>
        ),
      },
      {
        path: 'pricing',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PricingPage />
          </Suspense>
        ),
      },
      {
        // Legacy route - redirect to /pricing (pricing gate is now the main pricing form)
        path: 'request-pricing',
        element: <Navigate to="/pricing" replace />,
      },
      {
        path: 'pricing-request-success',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PricingRequestSuccess />
          </Suspense>
        ),
      },
      {
        path: 'proposal-success',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProposalSuccess />
          </Suspense>
        ),
      },
      {
        path: 'proposal-cancel',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProposalCancel />
          </Suspense>
        ),
      },
      {
        path: 'about',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AboutPage />
          </Suspense>
        ),
      },
      {
        path: 'resources',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResourcesPage />
          </Suspense>
        ),
      },
      {
        path: 'contact',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactPage />
          </Suspense>
        ),
      },
      {
        path: 'contact-success',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactSuccess />
          </Suspense>
        ),
      },
      {
        path: 'contact-personal-email',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactPersonalEmail />
          </Suspense>
        ),
      },
      {
        path: 'personal-email-notice',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PersonalEmailNotice />
          </Suspense>
        ),
      },
      {
        path: 'demo',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DemoVideoPage />
          </Suspense>
        ),
      },
      {
        path: 'book-a-consultation',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DemoPage />
          </Suspense>
        ),
      },
      {
        path: 'plan-your-marketplace',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaxPage />
          </Suspense>
        ),
      },
      { path: 'max', element: <Navigate to="/plan-your-marketplace" replace /> },
      // Product Pages
      {
        path: 'products',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductsPage />
          </Suspense>
        ),
      },
      {
        path: 'product-marketplace',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductMarketplacePage />
          </Suspense>
        ),
      },
      {
        path: 'product-services',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductServicesPage />
          </Suspense>
        ),
      },
      {
        path: 'product-launcher',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductLauncherPage />
          </Suspense>
        ),
      },
      {
        path: 'product-bookings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductBookingsPage />
          </Suspense>
        ),
      },
      {
        path: 'product-store',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductStorePage />
          </Suspense>
        ),
      },
      {
        path: 'product-custom',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductCustomPage />
          </Suspense>
        ),
      },
      {
        path: 'product-matchmaker',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductMatchmakerPage />
          </Suspense>
        ),
      },
      {
        path: 'white-label-ai-agent-marketplace',
        element: (
          <Suspense fallback={<PageLoader />}>
            <WhiteLabelAIAgentMarketplacePage />
          </Suspense>
        ),
      },
      {
        path: 'product-commodities',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductCommoditiesPage />
          </Suspense>
        ),
      },
      {
        path: 'enterprise',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EnterprisePage />
          </Suspense>
        ),
      },
      {
        path: 'support-hours',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SupportHoursPage />
          </Suspense>
        ),
      },
      {
        path: 'marketplace-insights',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPage />
          </Suspense>
        ),
      },
      {
        path: 'marketplace-insights/page/:page',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPage />
          </Suspense>
        ),
      },
      {
        path: 'marketplace-insights/:slug',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPostPage />
          </Suspense>
        ),
      },
      { path: 'blog', element: <LegacyBlogRedirect /> },
      { path: 'blog/:slug', element: <LegacyBlogRedirect /> },
      // Success Stories
      {
        path: 'success-stories',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SuccessStoriesPage />
          </Suspense>
        ),
      },
      {
        path: 'success-stories/:slug',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SuccessStoryPage />
          </Suspense>
        ),
      },
      {
        path: 'privacy-policy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        ),
      },
      {
        path: 'terms-and-conditions',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        ),
      },
      {
        path: 'cnxion-and-gdpr',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GdprPage />
          </Suspense>
        ),
      },
      {
        path: 'dpa',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DpaPage />
          </Suspense>
        ),
      },
      {
        path: 'end-user-licence-agreement',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EulaPage />
          </Suspense>
        ),
      },
      {
        path: 'third-party-services',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ThirdPartyServicesPage />
          </Suspense>
        ),
      },
      // Redirects from old URLs
      { path: 'privacy', element: <Navigate to="/privacy-policy" replace /> },
      { path: 'terms', element: <Navigate to="/terms-and-conditions" replace /> },
      { path: 'booking-thankyou', element: <Navigate to="/contact-success" replace /> },
      { path: 'test-sentry', element: <SentryDebugPage /> },
      {
        path: '*',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export const App: React.FC = () => (
  <ErrorBoundary>
    <LazyMotion features={domAnimation}>
      <RouterProvider router={router} />
    </LazyMotion>
  </ErrorBoundary>
);

export default App;
